import type { Chapter45Anchor } from './chapter45';
import type { Drug } from './components/drug-search';

// ============================================================================
// ChPL Extraction Types
// ============================================================================

/**
 * Extraction result for chapter 4.5 from a ChPL PDF
 */
export interface ChplExtraction {
  /** Whether extraction was successful */
  success: boolean;
  /** Page number in PDF where chapter 4.5 starts */
  page: number | null;
  /** Number of pages containing chapter 4.5 */
  pageCount: number | null;
  /** Full markdown content of chapter 4.5 */
  content: string | null;
}

// ============================================================================
// Extraction Discrepancy Types
// ============================================================================

/**
 * Type of discrepancy between PDF source and AI extraction
 */
export type DiscrepancyType = 'missing' | 'added' | 'changed';

/**
 * A single discrepancy between PDF source and AI extraction
 */
export interface ExtractionDiscrepancy {
  /** Type of discrepancy */
  type: DiscrepancyType;
  /** Text from PDF (null if 'added' - hallucination) */
  original: string | null;
  /** Text from AI extraction (null if 'missing') */
  extracted: string | null;
  /** Surrounding text for location context */
  context: string;
}

/**
 * Result of comparing extraction to source
 * null = comparison not completed (error/unchecked)
 * [] = perfect match (no discrepancies)
 * [...] = imperfect (discrepancies found)
 */
export interface ComparisonResult {
  /** List of discrepancies found (null = unchecked, empty = perfect) */
  discrepancies: ExtractionDiscrepancy[] | null;
  /** Validation metrics (timing, cost, tokens) */
  metrics?: LlmCallMetrics | null;
}

// ============================================================================
// Extraction Metrics Types
// ============================================================================

/**
 * Metrics for a single LLM API call
 */
export interface LlmCallMetrics {
  /** Duration in milliseconds */
  duration_ms: number;
  /** Cost in USD */
  cost_usd: number;
  /** Total tokens used */
  tokens: number;
}

/**
 * Metrics for Document Intelligence call
 */
export interface DICallMetrics {
  /** Duration in milliseconds */
  duration_ms: number;
  /** Cost in USD */
  cost_usd: number;
  /** Number of pages analyzed */
  pages_analyzed: number;
  /** Number of tables found */
  tables_found: number;
}

/**
 * Metrics for Step 3 table extraction (includes DI + LLM breakdown)
 */
export interface TableStepMetrics {
  /** Document Intelligence metrics (optional - not all tables use DI) */
  di?: DICallMetrics;
  /** LLM call metrics */
  llm: LlmCallMetrics;
  /** Combined total for this table */
  total: LlmCallMetrics;
}

/**
 * Full extraction metrics stored in database
 * Contains metrics for both extraction and validation steps
 */
export interface ExtractionMetrics {
  /** Metrics from the extraction LLM call (legacy single-prompt approach) */
  extraction?: LlmCallMetrics;
  /** Metrics from the validation/comparison LLM call */
  validation?: LlmCallMetrics;
  /** Metrics from the new multi-step pipeline */
  pipeline?: {
    /** Step 1: Structure analysis */
    step1?: LlmCallMetrics;
    /** Step 2: Text formatting */
    step2?: LlmCallMetrics;
    /** Step 3: Table extractions (by table ID, with DI + LLM breakdown) */
    step3_tables?: Record<string, TableStepMetrics>;
    /** Aggregated totals */
    total: LlmCallMetrics;
  };
}

// ============================================================================
// Extraction Run Types
// ============================================================================

/**
 * Status of an extraction run
 */
export type ExtractionRunStatus =
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

/**
 * Parameters for starting an extraction run
 */
export interface ExtractionRunParams {
  /** Drug IDs to process. Empty = all pending. */
  drugIds?: string[];
  /**
   * @deprecated Legacy field from older runs. Use drugIds instead.
   * Kept for backward compatibility with existing production data.
   */
  chplIds?: string[];
  /** Model used for extraction */
  model?: string;
  /** Any additional configuration */
  [key: string]: unknown;
}

/**
 * Summary of an extraction run stored in database
 */
export interface ExtractionRunSummary {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: ExtractionRunStatus;
  params: ExtractionRunParams;
  triggeredBy: string | null;
  totalDrugs: number;
  /** Real content events (llm + cross_section_dedup outcomes). */
  successful: number;
  /** Hash-match verifications — no content change (chapter_45_verified outcomes). */
  verified: number;
  failed: number;
  perfectCount: number;
  imperfectCount: number;
  totalCostUsd: number | null;
  totalTokens: number | null;
}

/**
 * Database row for chpl_extractions table
 */
export interface ChplExtractionRow {
  id: number;
  section_id: number | null;
  chapter_45_content: string | null;
  chapter_45_page: number | null;
  chpl_version_key: string | null;
  extraction_model: string | null;
  extracted_at: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Drug Interaction Types
// ============================================================================

/**
 * Severity levels for drug interactions
 */
export type InteractionSeverity =
  | 'critical'
  | 'important'
  | 'moderate'
  | 'minor';

/**
 * Represents a pair of drugs being analyzed
 */
export interface DrugPair {
  drug1: Drug;
  drug2: Drug;
}

/**
 * Quote from ChPL (drug leaflet) supporting the interaction
 */
export interface ChplQuote {
  /** Drug ID in our database */
  drugId?: string;
  /** ChPL ID used for extraction lookup (e.g., "NAR-3346") */
  chplId?: string;
  /** Drug name (e.g., "Klacid") */
  drugName: string;
  /** Active substance (e.g., "Klarytromycyna") */
  activeSubstance?: string;
  /** Drug strength/dose (e.g., "500 mg") */
  strength?: string;
  /** Drug form (e.g., "Tabletki powlekane") */
  form?: string;
  /** The actual quote text(s) - can be multiple snippets from different parts of the same ChPL */
  texts: string[];
  /**
   * Precomputed highlight anchors for the chapter 4.5 viewer.
   * These are computed server-side against the parsed chapter blocks and persisted in cache.
   */
  chapter45Anchors?: Chapter45Anchor[];
  /** URL to the ChPL document */
  url?: string;
  /** When the source document was last updated (from drug_documents.updated_at) */
  documentUpdatedAt?: string;
  /** When the last successful sync run completed */
  lastSyncCheckDate?: string;
}

/**
 * Options when opening the ChPL viewer (from list vs from interaction quote).
 */
export interface ViewChplOptions {
  /** Open in PDF tab instead of markdown */
  showPdf?: boolean;
  /** When true, scroll/navigate to chapter 4.5 (e.g. when opened from a quote). When false, start at beginning (e.g. when opened from drug list). */
  scrollToChapter45?: boolean;
  /** Whether the drug has a ChPL PDF available */
  hasChplPdf?: boolean;
  /** Whether the drug has an ulotka (patient leaflet) available */
  hasUlotka?: boolean;
}

/**
 * A confirmed interaction between two drugs
 */
export interface DrugInteraction {
  /** The pair of drugs involved */
  pair: DrugPair;
  /** Whether an interaction exists */
  hasInteraction: true;
  /** Severity level of the interaction */
  severity: InteractionSeverity;
  /** Detailed summary of the interaction and its implications */
  summary: string;
  /** Quotes from ChPL documents supporting this interaction */
  chplQuotes: ChplQuote[];
}

/**
 * A pair of drugs with no known interaction
 */
export interface NoInteraction {
  /** The pair of drugs checked */
  pair: DrugPair;
  /** No interaction found */
  hasInteraction: false;
}

/**
 * Result of checking a drug pair - either interaction or no interaction
 */
export type InteractionCheckResult = DrugInteraction | NoInteraction;

/**
 * Summary statistics for interaction analysis
 */
export interface InteractionsSummary {
  totalPairs: number;
  interactionsFound: number;
  criticalCount: number;
  importantCount: number;
  moderateCount: number;
  minorCount: number;
}

/**
 * Full results of analyzing drug interactions
 */
export interface InteractionAnalysisResults {
  /** All drugs being analyzed */
  drugs: Drug[];
  /** Results for each pair */
  results: InteractionCheckResult[];
  /** Summary statistics */
  summary: InteractionsSummary;
}

/**
 * Helper to get severity display properties (colours only).
 *
 * User-facing labels live in the `interactions` i18n namespace:
 * `severity.{critical|important|moderate|minor}.{label,labelFull,description}`.
 */
export function getSeverityConfig(severity: InteractionSeverity) {
  const configs = {
    critical: {
      bgColor: 'bg-red-50 dark:bg-red-950/50',
      borderColor: 'border-red-500 dark:border-red-400',
      textColor: 'text-red-700 dark:text-red-300',
      badgeColor:
        'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/50 dark:text-red-300 dark:border-red-700',
      iconColor: 'text-red-500 dark:text-red-400',
      shadowColor:
        'shadow-[2px_2px_0_0_#ef4444] dark:shadow-[2px_2px_0_0_#f87171]',
      linkColor:
        'text-red-800 decoration-red-400 hover:text-red-900 hover:decoration-red-700 dark:text-red-200 dark:decoration-red-600 dark:hover:text-red-100 dark:hover:decoration-red-400',
    },
    important: {
      bgColor: 'bg-orange-50 dark:bg-orange-950/50',
      borderColor: 'border-orange-500 dark:border-orange-400',
      textColor: 'text-orange-700 dark:text-orange-300',
      badgeColor:
        'bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-700',
      iconColor: 'text-orange-500 dark:text-orange-400',
      shadowColor:
        'shadow-[2px_2px_0_0_#f97316] dark:shadow-[2px_2px_0_0_#fb923c]',
      linkColor:
        'text-orange-800 decoration-orange-400 hover:text-orange-900 hover:decoration-orange-700 dark:text-orange-200 dark:decoration-orange-600 dark:hover:text-orange-100 dark:hover:decoration-orange-400',
    },
    moderate: {
      bgColor: 'bg-yellow-50 dark:bg-yellow-950/50',
      borderColor: 'border-yellow-500 dark:border-yellow-400',
      textColor: 'text-yellow-700 dark:text-yellow-300',
      badgeColor:
        'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-700',
      iconColor: 'text-yellow-500 dark:text-yellow-400',
      shadowColor:
        'shadow-[2px_2px_0_0_#eab308] dark:shadow-[2px_2px_0_0_#facc15]',
      linkColor:
        'text-yellow-800 decoration-yellow-400 hover:text-yellow-900 hover:decoration-yellow-700 dark:text-yellow-200 dark:decoration-yellow-600 dark:hover:text-yellow-100 dark:hover:decoration-yellow-400',
    },
    minor: {
      bgColor: 'bg-blue-50 dark:bg-blue-950/50',
      borderColor: 'border-blue-500 dark:border-blue-400',
      textColor: 'text-blue-700 dark:text-blue-300',
      badgeColor:
        'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700',
      iconColor: 'text-blue-500 dark:text-blue-400',
      shadowColor:
        'shadow-[2px_2px_0_0_#3b82f6] dark:shadow-[2px_2px_0_0_#60a5fa]',
      linkColor:
        'text-blue-800 decoration-blue-400 hover:text-blue-900 hover:decoration-blue-700 dark:text-blue-200 dark:decoration-blue-600 dark:hover:text-blue-100 dark:hover:decoration-blue-400',
    },
  } as const;

  return configs[severity];
}

/**
 * Generate unique key for a drug pair (order-independent)
 */
export function getPairKey(pair: DrugPair): string {
  const ids = [pair.drug1.id, pair.drug2.id].sort();
  return `${ids[0]}_${ids[1]}`;
}
